import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import moment from "moment"

import { Card, Col, Row } from 'react-bootstrap';
import Swal from 'sweetalert2';
import CircleLoader from "react-spinners/CircleLoader";

import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';


import { 
	fetchEpstResultByCertificationId, 
	fetchEpstResultFinished,
	fetchEpstResultsByCertificationId, 

	fetchEpstScoreStatisticsByCertification
} from 'api/certifications/epst/results';

import { 
	generateEmetResult,
	generateEpstResult,
	updateCertFileByCertificationId,
	updateEpstCertFileByCertificationId,
} from 'api/certifications/certifications';

import { alert, exportEpstEstadistico, exportEvaluatedEpst, getCandidateType, getCertificationStatus, getCertificationType, handleApiError } from 'utils';

import Header from './header/Header';
import ResultsColumns from './columns/ResultsColumns';
import { fetchEpstQuestionsReportHeaders } from 'api/certifications/epst/questions';


const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "red"
};

const ResultsTable = () => {
    const token = useSelector(state => state.authentication.user?.token);
	const navigate = useNavigate();
    
    const [certifications, setCertifications] = useState([]); 
	const [rows, setRows] = useState([]); 

	const [action, setAction] = useState("");
    const [questionId, setQuestionId] = useState(0);

	const [loading, setLoading] = useState(false);

	const [selectedRows, setSelectedRows] = useState({});

    useEffect(() => {
		if (certifications.length > 0) {
            const formattedCertifications = certifications.map((certification, index) => {
			
                return {
                    index: index++,
					certification_id: certification.certification_id,
					username: certification.username,
					name: certification.name,
					email: certification.email,
					college_name: certification.college,
					certification_type: getCertificationType(certification.certification_type),
					candidate_type: getCandidateType(certification.candidate_type),
					assigned_date: moment(certification.assigned_date).format("DD/MM/YYYY"),
					completed_date: moment(certification.completed_date).format("DD/MM/YYYY HH:mm:ss"),
					status: getCertificationStatus(certification.status),
					total_score: certification.total_score,
					total_percentage: `${certification.total_percentage}%`,
					level: certification.level,
					grammar_score: certification.grammar_score,
					grammar_percentage: `${certification.grammar_percentage}%`,
					reading_score: certification.reading_score,
					reading_percentage: `${certification.reading_percentage}%`,
					listening_score: certification.listening_score,
					listening_percentage: `${certification.listening_percentage}%`,
					writing_score: certification.writing_score,
					writing_percentage: isNaN(certification.writing_percentage) ? certification.writing_percentage : `${certification.writing_percentage}%`,
					speaking_score: certification.speaking_score,
					speaking_percentage: isNaN(certification.speaking_percentage) ? certification.speaking_percentage : `${certification.speaking_percentage}%`

                };
            });
			setRows(formattedCertifications);
		}
		// eslint-disable-next-line
	}, [certifications]);

	
	const loadEpstResultFinished = useCallback(async () => {
		try {
			setLoading(true);
			const { data } = await fetchEpstResultFinished(token);
			setCertifications(data.message);
		} catch (error) {
			handleApiError(error);
			setLoading(false);
		} finally {
			setLoading(false);
		}
	}, [token]);


	useEffect(() => {
		if (token) loadEpstResultFinished();
	}, [token, loadEpstResultFinished]);
	
	

	const handleEmetResultByCertificationId = async (certification_id, name, email, total_score, level, completed_date) => {
		try {
			const { data } = await fetchEpstResultByCertificationId(certification_id, token);
			if(data.status === 200) {
				if (!data.message.every(item => item.score >= 0)) {
					alert(
						"Evaluación incompleta",
						"Todavía faltan secciones por calificar. Debes asignar un puntaje a todas las secciones antes de enviar el certificado.",
						"warning"
					);
					return; 
				} else {
					handleEpstResultsByCertificationId(certification_id, name, email, total_score, level, completed_date);
				}	
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	const handleEpstResultsByCertificationId = async (certification_id, name, email, total_score, level, completed_date) => {
		try {
			const { data } = await fetchEpstResultsByCertificationId(certification_id, token);
			if(data.status === 200) {
				const certificationFile = data?.message?.certification_file;
				console.log(certificationFile);
				if (!certificationFile) {
					Swal.fire({
						title: "Archivo de certificación no encontrado",
						text: "El archivo de certificación aún no ha sido generado. ¿Deseas generarlo ahora?",
						icon: "warning",
						confirmButtonText: "Generar archivo",
						cancelButtonText: "Cancelar",
						showCancelButton: true,
						showCloseButton: true,
						allowOutsideClick: false,
						allowEscapeKey: false,
						allowEnterKey: false,
						reverseButtons: true
					}).then((result) => {
						if (result.isConfirmed) {
							moment.locale("en");
							const formattedDate = moment(completed_date, "DD/MM/YYYY HH:mm:ss").format("dddd, DD MMMM YYYY");
							const payload = {
								name: name,
								date: formattedDate,
								score: total_score,
								title: level,
								email: email
							}
							handleEpstGenerateResult(certification_id, payload, name);
						}
					});
				} else {
					const encodedFilename = encodeURIComponent(certificationFile);
					window.open(`${process.env.REACT_APP_CERTIFICATIONS_API}/files/download/2/${encodedFilename}`, "_blank", "noopener,noreferrer");
				}
			}
		} catch (error) {
			handleApiError(error);
		}
	}

	const handleEpstGenerateResult = async (certification_id, payload, name) => {
		try {
			const { data } = await generateEpstResult(payload, token);
			if(data.status === 200) {
				const payload_1 = {
					certification_file: data.output_file
				}
				handleUpdateEpstCertFileByCertificationId(certification_id, payload_1, name);
			}
		} catch (error) {
			handleApiError(error);
		}
	};



	const handleUpdateEpstCertFileByCertificationId = async (certification_id, payload, name) => {
		try {
			const { data } = await updateEpstCertFileByCertificationId(certification_id, payload, token);
			if(data.status === 200) {
				Swal.fire({
					title: "¡Certificado generado y enviado!",
					text: `El certificado ha sido generado correctamente y enviado al candidato ${name}.`,
					icon: "success",
					confirmButtonText: "Aceptar",
					showCloseButton: true,
					allowOutsideClick: false,
					allowEscapeKey: false,
					allowEnterKey: false
				});
			}
		} catch (error) {
			handleApiError(error);
		}
	};



	const handleExport = async () => {
		Swal.fire({
			title: '¿Deseas descargar el reporte de certificaciones?',
			text: 'Se generará un archivo con la información detallada de las certificaciones evaluadas.',
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) 
				exportEvaluatedEpst(rows);
		}).catch(() => {
			return false;
		})
	}

	const handleOpenEvaluation = (certification_id, name, email, college_name, certification_type, candidate_type, completed_date, status) => {
		let certification = {
			id: certification_id, 
			name: name,
			email: email, 
			college_name: college_name, 
			certification_type: certification_type, 
			candidate_type: candidate_type, 
			completed_date: completed_date, 
			status: status
		}

		navigate('/examiner/evaluate/epst', {
			state: { certification }
		});
	};

	const handleOpenCertification = (certification_id, name, email, college_name, certification_type, candidate_type, completed_date, status) => {
		let certification = {
			id: certification_id, 
			name: name,
			email: email, 
			college_name: college_name, 
			certification_type: certification_type, 
			candidate_type: candidate_type, 
			completed_date: completed_date, 
			status: status
		}

		navigate('/examiner/view/epst', {
			state: { certification }
		});
	};
	
	const handleShowFile = (certification_id, name, email, total_score, level, completed_date) => {
		handleEmetResultByCertificationId(certification_id, name, email, total_score, level, completed_date);
		
	};

	/**
	 * @function handleBulkAction
	 * @description
	 * Ejecuta acciones en lote según la selección (exportar, eliminar, etc).
	 *
	 * @param {string} action - Tipo de acción ('export', 'delete', etc.)
	 */
	const handleBulkAction = (action) => {
		if (action === 'export') {
			handleDownloadReports(selectedRows);
		} else if (action === 'delete') {
			//handleBulkDelete();
		}
	};

	const handleDownloadReports = async (rows) => {
		try {
			setLoading(true);

			const data_export = [[], []]; // [0]: Kids (type 1), [1]: Adults (type 2)

			const headersResponse = await Promise.all([
				fetchEpstQuestionsReportHeaders(1, token), // Kids
				fetchEpstQuestionsReportHeaders(2, token)  // Adults
			]);

			const headers = [headersResponse[0].data, headersResponse[1].data];

			for (const r of rows) {
				const typeIndex = r.candidate_type === "Kids" ? 0 : r.candidate_type === "Adults" ? 1 : null;
				if (typeIndex === null) continue;

				const type = typeIndex + 1; // Para las peticiones: 1 (Kids), 2 (Adults)
				const response = await fetchEpstScoreStatisticsByCertification(r.certification_id, type, token);
				const rowData = [r.email, r.name, ...response.data];

				data_export[typeIndex].push(rowData);
			}	
			
			// Exportar ambos si tienen datos
			if (data_export[0].length) exportEpstEstadistico(headers[0], data_export[0]);
			if (data_export[1].length) exportEpstEstadistico(headers[1], data_export[1]);
		} catch (error) {
			handleApiError(error);
			setLoading(false);
		} finally {
			setLoading(false);
		}
	}

	const handleDownloadReport = async (certification_id, candidate_type, email, nombre) => {
		try {
			let arrays_data = []
			setLoading(true);
			const type = candidate_type === "Kids" ? 1 : candidate_type === "Adults" ? 2 : null;
			const { data } = await fetchEpstQuestionsReportHeaders(type, token);
			if(data) {
				const response = await fetchEpstScoreStatisticsByCertification(certification_id, type, token);

				// Construir una fila con email, nombre y respuestas
				const row = [email, nombre, ...response.data];
				arrays_data.push(row);

				// Exportar todo el dataset
				exportEpstEstadistico(data, arrays_data);
			}
		} catch (error) {
			handleApiError(error);
			setLoading(false);
		} finally {
			setLoading(false);
		}
		
	};

	const handleDelete = (certification_id, name) => {
		Swal.fire({
			title: `¿Deseas eliminar la certificación de ${name}?`,
			text: "Una vez eliminado ya no estara disponible.",
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			//if (result.isConfirmed) 
				//confirmDeleteCertification(certification_id);
		}).catch(() => {
			return false;
		})
	};

	/*const confirmDeleteCertification = (certification_id) => {
		Swal.fire({
			title: "¿Estas seguro?",
			text: "Esta operacion no se puede deshacer.",
			icon: "warning",
			confirmButtonText: 'Eliminar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) {	
				//handleDeleteCertification(certification_id);
			}
		}).catch(() => {
			return false;
		})
	}*/

	/**
	 * @function handleSelectedChange
	 * @description
	 * Maneja el cambio de selección en la tabla. Extrae los `index` seleccionados desde `selectedData`
	 * y filtra las filas (`rows`) que coincidan con esos índices. Actualiza el estado `selectedRows` con las filas seleccionadas.
	 *
	 * Esta función está memorizada con `useCallback` para evitar recrearse innecesariamente,
	 * y depende del estado actual de `rows`.
	 *
	 * @param {Object} selectedData - Objeto con las claves de las filas seleccionadas, por ejemplo: { '0': true, '2': true }.
	 * Las claves representan los índices (`index`) de las filas seleccionadas.
	 *
	 * @returns {void}
	 */
	const handleSelectedChange = useCallback((selectedData) => {
		const selectedIds = Object.keys(selectedData).map(id => parseInt(id, 10));
		setSelectedRows(rows.filter(row => selectedIds.includes(row.index)));
	}, [rows]);

    return (
		<Row className="gx-3">
			{loading ? (
				<div className="sweet-loading" style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: 'rgba(255, 255, 255, 0.8)', // Puedes agregar un fondo semitransparente si lo deseas
					zIndex: 9999}} >
					<CircleLoader
						color={"#0a83e6"}
						loading={loading}
						cssOverride={override}
						size={150}
						aria-label="Loading..."
						data-testid="loader"
					/>
				</div>
			) : (
				<>
					<Col xxl={12} xl={12}>
						<AdvanceTableWrapper
							columns={ResultsColumns(handleOpenCertification, handleOpenEvaluation, handleDownloadReport, handleShowFile, handleDelete)}
							data={rows}
							selection
							selectionColumnWidth={52}
							sortable
							pagination
							perPage={10}
							rowCount={rows.length}
						>
							<Card>
								<Card.Header className="px-0">
									<Header table handleSelectedChange={handleSelectedChange} handleExport={handleExport} onBulkAction={handleBulkAction} />
								</Card.Header>
								<Card.Body className="p-0">
									<AdvanceTable
										table
										headerClassName="bg-light text-800 align-middle"
										rowClassName="btn-reveal-trigger align-middle"
										tableProps={{
											size: 'sm',
											className: 'fs--1 mb-0 overflow-hidden'
										}}
									/>
								</Card.Body>
								<Card.Footer>
									<AdvanceTablePagination table />
								</Card.Footer>
							</Card>
						</AdvanceTableWrapper>
					</Col>
				</>
			)}
		</Row>
	);
};

export default ResultsTable;

